import {
  useCreateStopList,
  useStopLists,
  useStopListSettings,
  useUpdateStopList,
  useUpdateStopListSettings,
  useVerifyStopListFile,
} from "@/api/stop-list.api";
import UploadStopListModal from "@/components/campaign/basic/UploadStopListModal";
import { Button } from "@/components/generic/Button";
import { Search as SearchComponent } from "@/components/generic/Search";
import {
  SelectFilter,
  useSelectFilter,
} from "@/components/generic/SelectFilter";
import { createTypedTable } from "@/components/generic/Table";
import { Focusable, Tooltip } from "@/components/generic/Tooltip";
import { PageContent } from "@/components/layout/MainWindow";
import { PageHeader } from "@/components/layout/PageHeader";
import { StickyElement } from "@/components/layout/StickyElement";
import { useHorizontalScrollSync } from "@/hooks/use-scroll-sync";
import { StopList } from "@/types/campaign";
import { formatDate2 } from "@/utils/dates";
import { createFileRoute, NavigateOptions } from "@tanstack/react-router";
import { debounce } from "lodash-es";
import React, { useCallback, useRef, useState } from "react";
import {
  FileTrigger,
  SortDescriptor,
  SortDirection,
} from "react-aria-components";
import { twMerge } from "tailwind-merge";

export const Route = createFileRoute("/_dashboard/settings/_admin/stop-lists/")(
  {
    component: RouteComponent,
    staticData: {
      crumb: "Stoplister",
    },
  },
);

const typedTable = createTypedTable<StopList>();

function RouteComponent() {
  const stopListsQuery = useStopLists();
  const stopListSettingsQuery = useStopListSettings();
  const createStopList = useCreateStopList();
  const updateStopList = useUpdateStopList();
  const updateStopSetting = useUpdateStopListSettings();
  const verifyFile = useVerifyStopListFile();

  const { TableHeader, TableBody } = typedTable;

  const [hoveredId, setHoveredId] = useState<string | number | null>(null);
  const [isUploadOpen, setUploadOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState("");
  const [mandatory, setMandatory] = useState(true);
  const [showFileFormatError, setShowFileFormatError] = useState(false);
  const [searchString, setSearchString] = useState("");
  const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>({
    column: "name",
    direction: "ascending" as SortDirection,
  });
  const [validCount, setValidCount] = useState(0);
  const headerScrollRef = useRef<HTMLDivElement>(null);
  const bodyScrollRef = useRef<HTMLDivElement>(null);
  useHorizontalScrollSync(bodyScrollRef, headerScrollRef);

  const [debouncedSearchString, setDebouncedSearchString] = useState("");

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSetSearch = useCallback(
    debounce((value: string) => setDebouncedSearchString(value), 300),
    [],
  );

  const availableFilter = useSelectFilter({
    items: [
      { key: "enabled", label: "Automatisk", value: "enabled" },
      { key: "disabled", label: "Manuelt", value: "disabled" },
    ],
  });

  const visibilityFilter = useSelectFilter({
    items: [
      { key: "mandatory", label: "Synlig", value: true },
      { key: "optional", label: "Skjult", value: false },
    ],
  });

  const typeFilter = useSelectFilter({
    items: [
      { key: "default", label: "Dynamisk", value: "default" },
      { key: "uploaded", label: "Statisk", value: "uploaded" },
    ],
  });

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchString(value);
    debouncedSetSearch(value);
  };

  const buttonRef = useRef<HTMLButtonElement | null>(null);

  const handleDrop = async (files: FileList | null) => {
    if (!files?.length) return;

    buttonRef.current?.blur();

    const file = files[0];
    try {
      const resp = await verifyFile.mutateAsync(file);
      const hasErrors = resp?.files?.[0]?.errors?.length;
      if (hasErrors) {
        setShowFileFormatError(true);
      } else {
        setValidCount(resp.valid);
        setFile(file);
        setUploadOpen(true);
      }
    } catch (e) {
      setShowFileFormatError(true);
    }
  };

  const handleUpload = () => {
    if (!file || !name) return;
    const formData = new FormData();
    formData.append("files", file);
    setUploadOpen(false);
    createStopList.mutate(
      { formData, name, mandatory },
      {
        onError: (error) => {
          if (
            error?.message?.includes("format") ||
            error?.message?.includes("CVR")
          ) {
            setShowFileFormatError(true);
          }
        },
        onSuccess: () => {
          setFile(null);
          setName("");
        },
      },
    );
  };

  const columns = typedTable.createColumns([
    {
      id: "name",
      name: "Navn",
      isRowHeader: true,
      width: 150,
      renderCell: (row) => {
        return (
          <div className="flex items-center gap-2">
            <span className="text-xs">{row.name}</span>
          </div>
        );
      },
      rowClassName: "h-9",
    },
    {
      id: "type",
      name: "Type",
      width: 120,
      rowClassName: "h-9",
      renderCell: (row) => (
        <span className="text-xs">
          {row.campaignList === true || row.uniqueId === "virtual-all-leads"
            ? "Dynamisk"
            : "Statisk"}
        </span>
      ),
    },
    {
      id: "visibility",
      name: "Synlighed",
      width: 120,
      renderCell: (row) => {
        if (row.uniqueId === "virtual-all-leads") {
          return <span className="text-xs">-</span>;
        }

        const isVisible = !row.hidden;
        return (
          <div className="flex items-center gap-2">
            <span
              className={twMerge(
                "text-xs",
                !isVisible && "text-theme-on-background-variant",
              )}
            >
              {isVisible ? "Synlig" : "Skjult"}
            </span>
          </div>
        );
      },
      rowClassName: "h-9",
    },
    {
      id: "alwaysEnabled",
      name: "Aktivering",
      width: 100,
      renderCell: (row) => {
        const isVirtual = row.id === "virtual-all-leads";
        const isUpdatingStopList =
          updateStopList.isPending && updateStopList.variables?.id === row.id;
        const isUpdatingSettings = updateStopSetting.isPending;
        const isLoading =
          isUpdatingStopList || (isVirtual && isUpdatingSettings);

        const globalEnabled = Boolean(
          stopListSettingsQuery.data?.excludeAlreadyDelivered,
        );

        const isEnabled = isVirtual ? globalEnabled : row.mandatory;

        return (
          <div className="flex items-center gap-2">
            <span className="text-xs">
              {isEnabled ? "Automatisk" : "Manuelt"}
            </span>
          </div>
        );
      },
      rowClassName: "h-9",
    },
    {
      id: "companies",
      name: "Firmaer",
      width: 55,
      rowClassName: "h-9 text-right",
      renderCell: (row) => (
        <span className="block w-full text-right text-xs">
          {row.uniqueId === "virtual-all-leads" ? "-" : row.count || 0}
        </span>
      ),
      headerClassName: "text-right",
    },
    {
      id: "campaigns",
      name: "Kampagner",
      width: 70,
      rowClassName: "h-9",
      renderCell: (row) => (
        <span className="block w-full text-right text-xs">
          {row.uniqueId === "virtual-all-leads" ? "-" : row.campaignCount || 0}
        </span>
      ),
      headerClassName: "text-right",
    },
    {
      id: "uploadedAt",
      name: "Oprettet",
      width: 60,
      rowClassName: "h-9",
      renderCell: (row) => (
        <span className="block w-full text-right text-xs">
          {row.id === "virtual-all-leads"
            ? "-"
            : row.createdAt
              ? formatDate2(new Date(row.createdAt))
              : "-"}
        </span>
      ),
      headerClassName: "text-right",
    },
  ]);
  const filteredItems = (): StopList[] => {
    const baseItems = stopListsQuery.data || [];

    const allItems: StopList[] = [...baseItems];

    const filtered = allItems.filter((item) => {
      if (
        debouncedSearchString &&
        !item.name.toLowerCase().includes(debouncedSearchString.toLowerCase())
      ) {
        return false;
      }

      if (
        typeFilter.selectedKeys !== "all" &&
        typeFilter.selectedKeys instanceof Set &&
        typeFilter.selectedKeys.size > 0
      ) {
        const isDefault =
          item.uniqueId === "virtual-all-leads" || item.campaignList === true;
        const hasDefault = typeFilter.selectedKeys.has("default");
        const hasUploaded = typeFilter.selectedKeys.has("uploaded");

        if (isDefault && !hasDefault) return false;
        if (!isDefault && !hasUploaded) return false;
      }

      if (
        visibilityFilter.selectedKeys !== "all" &&
        visibilityFilter.selectedKeys instanceof Set &&
        visibilityFilter.selectedKeys.size > 0
      ) {
        const isVisible = item.id === "virtual-all-leads" || !item.hidden;
        const hasVisible = visibilityFilter.selectedKeys.has("mandatory");
        const hasHidden = visibilityFilter.selectedKeys.has("optional");
        if (isVisible && !hasVisible) return false;
        if (!isVisible && !hasHidden) return false;
      }

      if (
        availableFilter.selectedKeys !== "all" &&
        availableFilter.selectedKeys instanceof Set &&
        availableFilter.selectedKeys.size > 0
      ) {
        const globalEnabled = Boolean(
          stopListSettingsQuery.data?.excludeAlreadyDelivered,
        );
        const isAlwaysEnabled =
          item.uniqueId === "virtual-all-leads"
            ? globalEnabled
            : item.mandatory;

        const hasAlwaysEnabled = availableFilter.selectedKeys.has("enabled");
        const hasNotAlwaysEnabled =
          availableFilter.selectedKeys.has("disabled");

        if (isAlwaysEnabled && !hasAlwaysEnabled) return false;
        if (!isAlwaysEnabled && !hasNotAlwaysEnabled) return false;
      }

      return true;
    });

    const virtualList = filtered.find(
      (item) => item.id === "virtual-all-leads",
    );
    const otherItems = filtered.filter(
      (item) => item.id !== "virtual-all-leads",
    );

    return virtualList
      ? [virtualList, ...otherItems.reverse()]
      : otherItems.reverse();
  };
  const buildItemLink = useCallback((item: StopList): NavigateOptions => {
    return {
      to: "/settings/stop-lists/$id",
      params: { id: item.uniqueId },
      search: { name: item.name },
    };
  }, []);

  return (
    <div className="mb-4 flex h-full flex-col">
      <PageHeader
        title="Stoplister"
        actions={
          <div className="flex items-center gap-3">
            <Tooltip
              placement="bottom right"
              arrowPosition="bottom right"
              arrow
              offset={20}
              className="mb-2 flex max-w-3xs items-center self-start rounded-sm p-4"
              tooltipText={
                <div className="flex w-60 flex-col gap-2">
                  <span className="text-theme-on-glass text-xs font-semibold">
                    Opret ved at uploade
                  </span>
                  <span className="text-theme-on-glass text-xs">
                    Upload Excel- eller CSV-fil.<br></br>
                    Hver række skal indeholde et 8-cifret CVR-nummer.
                  </span>
                </div>
              }
            >
              <Focusable className="flex cursor-pointer items-center self-start rounded-sm">
                <FileTrigger
                  onSelect={(f) => {
                    event?.stopPropagation();
                    handleDrop(f);
                  }}
                  acceptedFileTypes={[
                    ".csv",
                    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                    "application/vnd.ms-excel",
                  ]}
                >
                  <Button
                    ref={buttonRef}
                    animate
                    onClick={(e) => e.stopPropagation()}
                    className="px-10 text-sm font-medium"
                  >
                    <div className="flex items-center gap-2.5">
                      <span className="text-sm font-medium">
                        Opret stopliste
                      </span>
                    </div>
                  </Button>
                </FileTrigger>
              </Focusable>
            </Tooltip>
          </div>
        }
      />

      <PageContent className="flex h-full flex-col">
        <div className="relative grid *:col-start-1 *:row-start-1">
          <typedTable.Table
            buildItemLink={buildItemLink}
            hoveredId={hoveredId}
            setHoveredId={setHoveredId}
            aria-label="Stoplister"
            columns={columns}
            items={filteredItems()}
            setSortDescriptor={setSortDescriptor}
            sortDescriptor={sortDescriptor}
          >
            <StickyElement
              id="scroll-headers"
              stickyProps={{ className: "bg-theme-background" }}
            >
              <div className="px-7 xl:px-14">
                <div className="flex items-center gap-x-4 py-6">
                  <SearchComponent
                    className="-mx-2 grow"
                    value={searchString}
                    onChange={handleSearchChange}
                  />
                  <div className="flex items-center gap-x-10">
                    <SelectFilter
                      buttonLabel="Type"
                      selectionMode="multiple"
                      items={typeFilter.items}
                      selectedKeys={typeFilter.selectedKeys}
                      onSelectionChange={typeFilter.setSelectedKeys}
                    />

                    <SelectFilter
                      buttonLabel="Synlighed"
                      selectionMode="multiple"
                      items={visibilityFilter.items}
                      selectedKeys={visibilityFilter.selectedKeys}
                      onSelectionChange={visibilityFilter.setSelectedKeys}
                    />
                    <SelectFilter
                      buttonLabel="Aktivering "
                      selectionMode="multiple"
                      items={availableFilter.items}
                      selectedKeys={availableFilter.selectedKeys}
                      onSelectionChange={availableFilter.setSelectedKeys}
                    />
                  </div>
                </div>
                <div className="border-separator mb-3" />
              </div>
            </StickyElement>
            <StickyElement
              id="scroll-headers"
              stickyProps={{ className: "bg-theme-background" }}
              className="overflow-x-scroll"
              ref={headerScrollRef}
            >
              <TableHeader />
            </StickyElement>
            <TableBody
              id="scroll-headers"
              ref={bodyScrollRef}
              className="min-w-0 overflow-x-scroll"
            />
          </typedTable.Table>
        </div>
      </PageContent>

      <UploadStopListModal
        isOpen={isUploadOpen}
        validCount={validCount}
        onClose={() => setUploadOpen(false)}
        file={file}
        name={name}
        setName={setName}
        mandatory={mandatory}
        setMandatory={setMandatory}
        onSave={() => {
          handleUpload();
        }}
        showErrorModal={showFileFormatError}
        setShowErrorModal={setShowFileFormatError}
      />
    </div>
  );
}
